import { type PropsWithChildren, type FC, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import RegisterLayout from '@/registration/component/Register/Layout';
import { Center, SkeletonCircle, SkeletonText } from '@chakra-ui/react';
import { UiButton, UiHStack, UiStack, uiStyles, UiText } from '@/lib/ui';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import BaseMessageBarError from '@/base/MessageBar/Error';

const RouteGuard: FC<PropsWithChildren> = ({ children }) => {
  const { eventId, tenantCode } = useRegisterRoute();
  const location = useLocation();
  const navigate = useNavigate();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);

  /**
   * @todo We don't recommend to write inline functions. It's hard to read and change (e.g. if we need to add more lines in the function body).
   */
  const emailPageUrl = useMemo(
    () => {
      return generatePageUrl('RegistrationRegisterEmail', { tenantCode, eventId });
    },
    [eventId, tenantCode]
  );

  const confirmationUrl = useMemo(
    () => {
      generatePageUrl('RegistrationRegisterConfirmation', { tenantCode, eventId })
    }
    , [eventId, tenantCode]);

  const onBackEmail = useCallback(() => navigate(emailPageUrl), [emailPageUrl, navigate]);

  if (groupRegistrationQuery?.error && ![emailPageUrl, confirmationUrl].includes(location.pathname)) {
    return (
      <RegisterLayout showShoppingCart={false}>
        <BaseMessageBarError borderRadius={uiStyles.borderRadius}>You are no longer signed in.</BaseMessageBarError>
        <UiHStack justifyContent={'flex-end'}>
          <UiButton colorScheme={'primary'} variant={'ghost'} p={0} mt={8} onClick={onBackEmail}>
            Back to the email page
          </UiButton>
        </UiHStack>
      </RegisterLayout>
    );
  }

  if (groupRegistrationQuery?.data && groupRegistrationQuery?.isLoading) { // loading registration
    return (
      <RegisterLayout showShoppingCart={false}>
        <SkeletonCircle size='16' />
        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
      </RegisterLayout>
    );
  }

  if ((groupRegistrationQuery?.isLoading)) { // loading registration
    return (
      <RegisterLayout showShoppingCart={false}>
        <SkeletonCircle size='16' />
        <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
      </RegisterLayout>
    );
  }
  if (!groupRegistrationQuery?.data) { // registration is null
    return (
      <RegisterLayout showShoppingCart={false}>
        <BaseMessageBarError borderRadius={uiStyles.borderRadius}>This page does not exist.</BaseMessageBarError>
        <UiHStack justifyContent={'flex-end'} pt={8}>
          <UiButton colorScheme={'primary'} onClick={onBackEmail}>
            Back to register page
          </UiButton>
        </UiHStack>
      </RegisterLayout>
    );
  }
  return children;
};

export default RouteGuard;

// /**
//  * @todo This is NOT a good component to include RouteGuard. Even if we want to do this, we should allow pages that don't need registration auth to work without it.
//  */
// const RegisterContainer: FC<PropsWithChildren> = ({children}) => (
//     <RouteGuard>
//       {children}
//     </RouteGuard>
// );
//
// export default RegisterContainer;
